import React, { useState } from 'react';
import { Upload, Button, message, Progress } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { uploadFile, downloadTemplate } from '../services/trackExportApi';

const ACCEPT = '.xls,.xlsx,.csv';

const STATUS_VALIDATION_FAILED = 2;

interface TrackExportUploadSectionProps {
  uploading?: boolean;
  uploadProgress?: number;
  currentTaskNo?: string | null;
  onUploadStart?: () => void;
  onUploadProgress?: (percent: number) => void;
  /** 上传流程结束（含校验失败落库） */
  onUploadComplete?: (payload: { taskNo: string; status: number; message: string }) => void;
  onUploadError?: () => void;
}

const TrackExportUploadSection: React.FC<TrackExportUploadSectionProps> = ({
  uploading = false,
  uploadProgress = 0,
  currentTaskNo = null,
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
}) => {
  const { t } = useTranslation();
  const [templateLoading, setTemplateLoading] = useState(false);

  const beforeUpload = (file: File): boolean => {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const allowed = ['.xls', '.xlsx', '.csv'];
    if (!allowed.includes(ext)) {
      message.error(t('central.trackExport.upload.formatError'));
      return false;
    }

    return true;
  };

  const handleUpload = async (file: File) => {
    if (!beforeUpload(file)) return false;

    onUploadStart?.();
    try {
      onUploadProgress?.(30);
      const result = await uploadFile(file);
      onUploadProgress?.(60);
      onUploadProgress?.(100);
      onUploadComplete?.({
        taskNo: result.taskNo,
        status: result.status,
        message: result.message,
      });
      if (result.status === STATUS_VALIDATION_FAILED) {
        message.warning(result.message || t('central.trackExport.upload.validationFailed'));
      } else {
        message.success(t('central.trackExport.upload.success'));
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('central.trackExport.upload.failed'));
      onUploadError?.();
    }

    return false;
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      await downloadTemplate();
      message.success(t('central.trackExport.template.downloadSuccess'));
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('central.trackExport.template.downloadFailed'));
    } finally {
      setTemplateLoading(false);
    }
  };

  return (
    <div className="mb-4 pt-4">
      <div className="text-lg font-bold mb-4">{t('central.trackExport.title')}</div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <Button
            type="default"
            icon={<DownloadOutlined />}
            loading={templateLoading}
            onClick={handleDownloadTemplate}
          >
            {t('central.trackExport.template.download')}
          </Button>
        </div>
        <Upload.Dragger
          accept={ACCEPT}
          showUploadList={false}
          beforeUpload={(file) => {
            handleUpload(file);
            return false;
          }}
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            {uploading ? t('central.trackExport.upload.uploading') : t('central.trackExport.upload.hint')}
          </p>
          <p className="ant-upload-hint">{t('central.trackExport.upload.formatHint')}</p>
        </Upload.Dragger>

        {uploading && (
          <div className="w-full">
            <Progress
              percent={uploadProgress}
              status="active"
              format={(percent) => `${percent}%`}
            />
            {currentTaskNo && (
              <div className="text-sm text-gray-600 mt-2">
                {t('central.trackExport.upload.taskProcessing', { taskNo: currentTaskNo })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackExportUploadSection;
